// native
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';

// addon
import { TranslateService } from '@ngx-translate/core';

// services
import { UsersService } from 'app/core/services/users.service';
import { OfficesService } from 'app/core/services/offices.service';
import { ErrorService } from 'app/core/services/error.service';
import { AuthService } from 'app/core/services/auth.service';
import { SsoService } from 'app/core/services/sso.service';

// models
import { CenterContainerTitle, IEmployee, IEmployeeRequest, Office, PaginatedItems } from '../../models';

// constants
import { EMPLOYEE_GROUPS } from '../../constants';

@Component({
    selector: 'app-user-form',
    templateUrl: './user-form.component.html',
    standalone: false
})
export class UserFormComponent implements OnInit, OnChanges {

  @Input() user: IEmployee;
  @Input() action: string;
  @Input() preselectedOfficeId?: number;

  @Output() close: EventEmitter<IEmployee> = new EventEmitter<IEmployee>();

  form: UntypedFormGroup;
  offices: Office[];
  employeeGroups = EMPLOYEE_GROUPS;

  title: CenterContainerTitle = {};

  isLoading: boolean = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private usersService: UsersService,
    private officesService: OfficesService,
    private errorService: ErrorService,
    private translateService: TranslateService,
    public authService: AuthService,
    private ssoService: SsoService
  ) { }

  ngOnInit() {
    this.isLoading = true;

    this.translateService.get('user').subscribe(() => this.setTranslations());

    this.officesService.getAll().pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((offices: PaginatedItems<Office>) => {
      this.offices = offices.results;
      this.initializeForm();
    }, error => this.errorService.handleError(error));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['action']?.currentValue)
      this.title.thin = changes['action']?.currentValue;
  }

  private setTranslations() {
    this.title.bold = this.translateService.instant('user');
  }

  public initializeForm() {
    const { first_name, last_name, username, email, phone_number, offices, groups, sso_id } = this.user || ({} as IEmployee);
    const isSsoNoEmail = this.authService.isSsoNoEmail;
    const isSsoEnabled = this.authService.isSsoEnabled;
    const isEdit = !!this.user;

    const formControls: Record<string, unknown> = {
      first_name: [first_name, Validators.required],
      last_name: [last_name, Validators.required],
      offices: [offices, Validators.required],
      employeeGroup: [groups ? groups[0] : null, Validators.required],
      phone_number: [phone_number],
    };

    if (isSsoNoEmail) {
      formControls['sso_id'] = [
        { value: sso_id, disabled: isEdit },
        isEdit ? [] : Validators.required
      ];
    }

    if (!isSsoNoEmail) {
      formControls['email'] = [email, Validators.compose([Validators.required, Validators.email])];
    }

    // Username is server-generated for no-email create; otherwise show it (readonly for SSO).
    if (!isSsoNoEmail || isEdit) {
      formControls['username'] = isSsoEnabled
        ? [{ value: username, disabled: true }]
        : [username, Validators.required];
    }

    this.form = this.formBuilder.group(formControls);

    if (!this.user && this.preselectedOfficeId)
      this.form.controls['offices'].setValue([this.preselectedOfficeId]);
    else if (!this.user)
      this.form.controls['offices'].setValue([this.offices[0]?.id]);

    if (!!this.user)
      this.form.markAllAsTouched();
  }

  public onSubmit(form: UntypedFormGroup) {
    if (!form.valid)
      return;

    const { first_name, last_name, username, email, phone_number, offices, employeeGroup, sso_id } = this.form.getRawValue();
    const isSsoNoEmail = this.authService.isSsoNoEmail;
    const isSsoEnabled = this.authService.isSsoEnabled;

    const groups = employeeGroup ? [employeeGroup] : employeeGroup;

    const body: IEmployeeRequest = {
      first_name,
      last_name,
      offices: this.authService.isOfficeAdmin ? offices.filter(office => this.offices.map(o => o.id).includes(office)) : offices,
      groups,
      phone_number: phone_number || null,
    };

    if (isSsoNoEmail) {
      if (!this.user)
        body.sso_id = sso_id;
    } else {
      body.email = email?.toLowerCase ? email.toLowerCase() : null;
      if (!isSsoEnabled)
        body.username = username;
    }

    const request = this.user
      ? this.usersService.update(this.user.id, body)
      : isSsoNoEmail
        ? this.ssoService.createSsoUser(body)
        : this.usersService.create(body);

    this.isLoading = true;
    request.pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(
      response => this.close.emit(response),
      error => this.errorService.handleUserError(this.form, error)
    );
  }
}
